// src/modules/customModels.js
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

/* ---------- helpers ---------- */
export function isLowPowerDevice(gl) {
  const uaMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  const saveData = navigator.connection?.saveData === true;
  const memGB = navigator.deviceMemory || 0;
  const cores = navigator.hardwareConcurrency || 0;
  const dpr = window.devicePixelRatio || 1;

  let gpu = '';
  try {
    const dbg = gl.getExtension('WEBGL_debug_renderer_info');
    if (dbg) gpu = gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL) || '';
  } catch { }
  const weakGPU = /(mali|adreno\s?[12]\d{2}|powervr|u?hd\s?graphics|intel\b|apple\sa\d)/i.test(gpu);
  const lowRam = memGB && memGB <= 4;
  const lowCores = cores && cores <= 4;
  const mobileHiDPR = uaMobile && dpr >= 3;
  return saveData || weakGPU || lowRam || lowCores || mobileHiDPR;
}

export const mat = {
  std: (color = 0xffffff, { roughness = 0.8, metalness = 0.0, transparent = false, opacity = 1 } = {}) =>
    new THREE.MeshStandardMaterial({ color, roughness, metalness, transparent, opacity }),
  glass: ({ color = 0xffffff, opacity = 0.2 } = {}) =>
    new THREE.MeshStandardMaterial({ color, roughness: 0.02, metalness: 0.0, transparent: true, opacity, depthWrite: false, side: THREE.FrontSide })
};

/* ---------- mapbox-aligned lighting like your reference ---------- */
function getMapboxLightDirection(preset, theme) {
  if (preset === 'dawn') return theme === 'monochrome' ? [120, 40] : [120, 50];
  if (preset === 'day') return [180, 70];
  if (preset === 'dusk') return theme === 'monochrome' ? [240, 30] : [240, 80];
  if (preset === 'night') return theme === 'monochrome' ? [270, 20] : [270, 20];
  return [180, 20];
}
function getMapboxLightIntensity(preset) {
  return ({ dawn: 1.7, day: 1.2, dusk: 1.2, night: 1.7 }[preset] ?? 1.2);
}
function getMapboxLightColor(preset, theme) {
  const colors = {
    dawn: 'hsl(33, 98%, 77%)',
    day: 'hsl(0, 0%, 100%)',
    dusk: theme === 'monochrome' ? 'hsl(30, 0%, 50%)' : 'hsl(30, 98%, 76%)',
    night: theme === 'monochrome' ? 'hsl(0, 0%, 0%)' : 'hsl(0, 0%, 29%)'
  };
  return colors[preset] || 'hsl(0,0%,100%)';
}
function sphericalToVector3(azimuthDeg, altitudeDeg, big = false) {
  const az = THREE.MathUtils.degToRad(azimuthDeg);
  const al = THREE.MathUtils.degToRad(altitudeDeg);
  const m = big ? 350 : 50;
  return new THREE.Vector3(
    Math.cos(al) * Math.sin(az) * m,
    Math.cos(al) * Math.cos(az) * m,
    Math.sin(al) * m
  );
}
function createDirectionalLightFromMap(map, big = false) {
  const theme = map.getConfigProperty?.('basemap', 'theme') || 'default';
  const preset = map.getConfigProperty?.('basemap', 'lightPreset') || 'day';
  const [az, alt] = getMapboxLightDirection(preset, theme);
  const dir = sphericalToVector3(az, alt, big);
  const color = new THREE.Color(getMapboxLightColor(preset, theme));
  const intensity = getMapboxLightIntensity(preset);

  const light = new THREE.DirectionalLight(color, intensity);
  light.position.copy(dir);
  light.castShadow = true;
  // generous context shadows if big=true
  light.shadow.mapSize.set(big ? 8192 : 4096, big ? 8192 : 4096);
  light.shadow.bias = -0.0005;
  light.shadow.normalBias = 0.01;
  light.shadow.camera.near = 0.1;
  light.shadow.camera.far = big ? 500 : 100;
  light.shadow.camera.left = big ? -500 : -100;
  light.shadow.camera.right = big ? 500 : 100;
  light.shadow.camera.top = big ? 200 : 100;
  light.shadow.camera.bottom = big ? -1000 : -100;
  return light;
}

// Full drop-in replacement
export function createGLTFLayer({
  id = 'gltf-layer',
  modelUrl,
  originLngLat,
  interaction = 'none',                 // 'none' | 'hover' | 'click' | 'both'
  pickFilter = (o) => o.isMesh && !/Glass/i.test(o.name || ''),
  meshStyle = null,                     // (mesh) => { mesh.material = ... }
  onHover = null,                       // (THREE.Intersection|null, layer) => {}
  onClick = null,                       // (THREE.Intersection|null, layer) => {}
  hoverStyle = 'material',              // 'material' | 'opacity'
  baseOpacity = 0.55,
  hoverOpacity = 0.85,
  selectedOpacity = 1.0,
  retainHighlightOnSelect = true,

  // Lighting / shadows / debug
  lightConfig = { color: 0xffffff, intensity: 1.2 },
  shadowConfig = {
    enabled: true,
    mapSize: 4096,
    bias: -0.0005,
    normalBias: 0.01,
    camera: { near: 0.5, far: 500, left: -150, right: 150, top: 150, bottom: -150 },
    autoFit: false,          // auto-fit shadow frustum to model bbox
    autoFitMargin: 20
  },
  debugLight = {
    key: '`',
    startVisible: false,
    helperSize: 10,
    persistKey: null         // optional localStorage key
  },
  shadowPlane = true,
  bigContextLight = false,

  // Lifecycle hooks
  onProgress = null,         // (ratio: 0..1 | null, xhr?: ProgressEvent) => void
  onLoaded = null            // (layer) => void
} = {}) {
  if (!modelUrl || !originLngLat) throw new Error('createGLTFLayer: modelUrl and originLngLat are required.');

  const mc = mapboxgl.MercatorCoordinate.fromLngLat(originLngLat, 0);
  const transform = {
    translateX: mc.x, translateY: mc.y, translateZ: mc.z,
    rotateX: 0, rotateY: 0, rotateZ: 0,
    scale: mc.meterInMercatorCoordinateUnits()
  };

  // Reusable highlight material (used when hoverStyle === "material")
  const highlightMaterial = mat.std(0xf9d235, { roughness: 0.6, metalness: 0.1 });

  const L = {
    id, type: 'custom', renderingMode: '3d',
    map: null, gl: null,
    scene: null, camera: null, renderer: null,
    model: null, modelGroup: null, modelLoaded: false,

    // picking & state
    raycaster: new THREE.Raycaster(),
    pickables: [],
    _hovered: null,
    _selected: new Set(),
    _enabled: true,

    // progress
    _lastProgress: null,

    onAdd(map, gl) {
      this.map = map;
      this.gl = gl;

      this.scene = new THREE.Scene();
      this.camera = new THREE.PerspectiveCamera(28, 1, 0.1, 1e6);
      this.modelGroup = new THREE.Group();
      this.scene.add(this.modelGroup);

      this.renderer = new THREE.WebGLRenderer({ canvas: map.getCanvas(), context: gl, antialias: true });
      this.renderer.autoClear = false;

      // Detect low-power and keep shadows off if needed
      const lowPower = isLowPowerDevice(gl);
      if (!lowPower) {
        this.renderer.shadowMap.enabled = true;
        this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
      }

      // Directional light aligned with the basemap light preset
      const dirLight = createDirectionalLightFromMap(map, bigContextLight);

      // --- Debug helpers
      const isTouch = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0) || (navigator.msMaxTouchPoints > 0);
      this._lightHelper = new THREE.DirectionalLightHelper(dirLight, debugLight.helperSize || 10);
      this._shadowCamHelper = new THREE.CameraHelper(dirLight.shadow.camera);

      let initialVisible = !!debugLight.startVisible;
      try {
        if (debugLight.persistKey) {
          const v = localStorage.getItem(debugLight.persistKey);
          if (v === '1') initialVisible = true;
          if (v === '0') initialVisible = false;
        }
      } catch {}

      if (isTouch && debugLight.startVisible !== true) {
        initialVisible = false; // don’t auto-show on touch unless forced
      }
      this._lightHelper.visible = initialVisible;
      this._shadowCamHelper.visible = initialVisible;

      this.scene.add(this._lightHelper);
      this.scene.add(this._shadowCamHelper);

      if (!isTouch && debugLight.key) {
        const toggleKey = String(debugLight.key).toLowerCase();
        this._onKeyDown = (ev) => {
          if ((ev.key || '').toLowerCase() !== toggleKey) return;
          const v = !this._lightHelper.visible;
          this._lightHelper.visible = v;
          this._shadowCamHelper.visible = v;
          try {
            if (debugLight.persistKey) localStorage.setItem(debugLight.persistKey, v ? '1' : '0');
          } catch {}
          this.map?.triggerRepaint?.();
        };
        window.addEventListener('keydown', this._onKeyDown);
      }

      this.setDebugLightVisible = (v) => {
        const vis = !!v;
        this._lightHelper.visible = vis;
        this._shadowCamHelper.visible = vis;
        try {
          if (debugLight.persistKey) localStorage.setItem(debugLight.persistKey, vis ? '1' : '0');
        } catch {}
        this.map?.triggerRepaint?.();
      };

      // Optional overrides
      if (lightConfig?.intensity != null) dirLight.intensity = lightConfig.intensity;
      if (lightConfig?.color != null) dirLight.color = new THREE.Color(lightConfig.color);

      // Shadows
      dirLight.castShadow = !!shadowConfig.enabled;
      if (shadowConfig.enabled) {
        const size = shadowConfig.mapSize || 4096;
        dirLight.shadow.mapSize.set(size, size);
        dirLight.shadow.bias = shadowConfig.bias ?? -0.0005;
        dirLight.shadow.normalBias = shadowConfig.normalBias ?? 0.01;

        const cam = dirLight.shadow.camera; // OrthographicCamera
        cam.near   = shadowConfig.camera?.near   ?? cam.near;
        cam.far    = shadowConfig.camera?.far    ?? cam.far;
        cam.left   = shadowConfig.camera?.left   ?? cam.left;
        cam.right  = shadowConfig.camera?.right  ?? cam.right;
        cam.top    = shadowConfig.camera?.top    ?? cam.top;
        cam.bottom = shadowConfig.camera?.bottom ?? cam.bottom;
        cam.updateProjectionMatrix();
      }

      this.scene.add(dirLight);
      this.scene.add(new THREE.AmbientLight(0xffffff, 1.2));
      this._light = dirLight;

      // Ground receiver (soft contact)
      if (shadowPlane) {
        const plane = new THREE.Mesh(
          new THREE.PlaneGeometry(15000, 15000),
          new THREE.ShadowMaterial({ opacity: 0.3 })
        );
        plane.rotation.x = 0;
        plane.position.y = 0;
        plane.receiveShadow = true;
        this.scene.add(plane);
      }

      // ---- Load model (with progress)
      const loader = new GLTFLoader();
      loader.load(
        modelUrl,
        (gltf) => {
          const root = gltf.scene;
          this.model = root;

          root.traverse((obj) => {
            if (!obj.isMesh) return;

            obj.castShadow = true;
            obj.receiveShadow = true;

            if (typeof meshStyle === 'function') meshStyle(obj);

            if (hoverStyle === 'opacity' && obj.material) {
              // clone to avoid shared material mutations
              obj.material = obj.material.clone();
              obj.material.transparent = true;
              obj.material.opacity = baseOpacity;
              obj._baseOpacity = baseOpacity;
              obj._hoverOpacity = hoverOpacity;
              obj._selectedOpacity = selectedOpacity;
            }

            if (pickFilter(obj)) this.pickables.push(obj);
          });

          this.modelGroup.add(root);
          this.modelLoaded = true;

          if (shadowConfig.enabled && shadowConfig.autoFit) {
            this.fitShadowToModel(shadowConfig.autoFitMargin ?? 20);
          }

          this._bindInteractions();

          // Loading done — notify
          this._lastProgress = 1;
          if (typeof onProgress === 'function') onProgress(1, null);
          if (typeof onLoaded === 'function') onLoaded(this);
        },
        (xhr) => {
          // Progress event may not be lengthComputable across origins
          let ratio = null;
          try {
            if (xhr && typeof xhr.loaded === 'number' && typeof xhr.total === 'number' && xhr.total > 0) {
              ratio = Math.max(0, Math.min(1, xhr.loaded / xhr.total));
            }
          } catch {}
          this._lastProgress = ratio;
          if (typeof onProgress === 'function') onProgress(ratio, xhr);
        },
        (err) => {
          console.error('[customModels] GLTF load error:', err);
          // error — hide progress
          if (typeof onProgress === 'function') onProgress(null, null);
        }
      );
    },

    onRemove() {
      const canvas = this.map?.getCanvas?.();
      this._unbindInteractions();
      if (this.renderer) {
        this.renderer.forceContextLoss?.();
        this.renderer.dispose();
      }
      this.modelGroup?.clear?.();
      this.scene?.clear?.();
      if (this._onKeyDown) {
        window.removeEventListener('keydown', this._onKeyDown);
        this._onKeyDown = null;
      }
    },

    render(gl, matrix) {
      const canvas = this.map.getCanvas();
      this.camera.aspect = canvas.width / canvas.height;
      this.camera.updateProjectionMatrix();

      const rotX = new THREE.Matrix4().makeRotationAxis(new THREE.Vector3(1, 0, 0), 0);
      const rotY = new THREE.Matrix4().makeRotationAxis(new THREE.Vector3(0, 1, 0), 0);
      const rotZ = new THREE.Matrix4().makeRotationAxis(new THREE.Vector3(0, 0, 1), 0);

      const m = new THREE.Matrix4().fromArray(matrix);
      const l = new THREE.Matrix4()
        .makeTranslation(transform.translateX, transform.translateY, transform.translateZ)
        .scale(new THREE.Vector3(transform.scale, -transform.scale, transform.scale))
        .multiply(rotX).multiply(rotY).multiply(rotZ);

      this.camera.projectionMatrix = m.multiply(l);
      this.camera.projectionMatrixInverse.copy(this.camera.projectionMatrix).invert();

      this.renderer.setSize(canvas.width, canvas.height, false);
      this.renderer.resetState();
      this.renderer.render(this.scene, this.camera);
      this.map.triggerRepaint();
    },

    /* ---------------- interactions ---------------- */
    _bindInteractions() {
      const canvas = this.map.getCanvas();

      if (interaction === 'hover' || interaction === 'both') {
        this._onMouseMove = (ev) => {
          if (!this._enabled) return;
          const r = canvas.getBoundingClientRect();
          const pt = { x: ev.clientX - r.left, y: ev.clientY - r.top };
          const hits = this._raycastAt(pt);
          const first = hits[0] || null;

          if (typeof onHover === 'function') onHover(first, this);

          const hovered = first?.object || null;
          if (hovered !== this._hovered) {
            // restore previous hovered if it wasn’t selected or if we don’t keep highlight on selected
            if (this._hovered) {
              const prev = this._hovered;
              const allowRestore = (!prev.selected) || (!retainHighlightOnSelect);
              if (allowRestore) {
                if (hoverStyle === 'opacity') {
                  prev.material.opacity = (prev._baseOpacity ?? baseOpacity);
                } else {
                  prev.material = prev._origMat || prev.material;
                }
              }
            }

            this._hovered = hovered || null;

            if (this._hovered) {
              // Don’t override selected look when retainHighlightOnSelect = true
              if (this._hovered.selected && retainHighlightOnSelect) {
                if (hoverStyle === 'opacity') {
                  const sel = this._hovered._selectedOpacity ?? selectedOpacity;
                  this._hovered.material.opacity = sel;
                } else {
                  // keep whatever material is set for selection
                }
              } else {
                if (hoverStyle === 'opacity') {
                  const target = (this._hovered._hoverOpacity ?? hoverOpacity);
                  this._hovered.material.opacity = target;
                } else {
                  this._hovered._origMat = this._hovered._origMat || this._hovered.material;
                  this._hovered.material = highlightMaterial;
                }
              }
            }
          }
        };
        canvas.addEventListener('mousemove', this._onMouseMove, { passive: true });
      }

      if (interaction === 'click' || interaction === 'both') {
        this._onClick = (ev) => {
          if (!this._enabled) return;
          const r = canvas.getBoundingClientRect();
          const pt = { x: ev.clientX - r.left, y: ev.clientY - r.top };
          const hits = this._raycastAt(pt);
          const first = hits[0] || null;

          if (typeof onClick === 'function') onClick(first, this);

          // Clicking empty space clears selection
          if (!first) {
            for (const o of this._selected) {
              o.selected = false;
              if (hoverStyle === 'opacity') o.material.opacity = (o._baseOpacity ?? baseOpacity);
              else o.material = o._origMat || o.material;
            }
            this._selected.clear();
            this._hovered = null;
            this.map?.triggerRepaint?.();
            return;
          }

          const obj = first.object;

          // single-select: clear previous
          for (const o of this._selected) {
            o.selected = false;
            if (hoverStyle === 'opacity') o.material.opacity = (o._baseOpacity ?? baseOpacity);
            else o.material = o._origMat || o.material;
          }
          this._selected.clear();

          // select the clicked one
          this._selected.add(obj);
          obj.selected = true;

          if (hoverStyle === 'opacity') {
            obj.material.opacity = (obj._selectedOpacity ?? selectedOpacity);
          } else {
            obj._origMat = obj._origMat || obj.material;
            obj.material = highlightMaterial;
          }

          // Don’t let leftover hover tweak the selected one
          this._hovered = obj;
        };
        canvas.addEventListener('click', this._onClick, { passive: true });
      }
    },

    _unbindInteractions() {
      const canvas = this.map?.getCanvas?.();
      if (!canvas) return;
      if (this._onMouseMove) canvas.removeEventListener('mousemove', this._onMouseMove);
      if (this._onClick) canvas.removeEventListener('click', this._onClick);
    },

    _screenToRay(point) {
      const ndc = {
        x: (point.x / this.map.transform.width) * 2 - 1,
        y: 1 - (point.y / this.map.transform.height) * 2
      };
      const invProj = this.camera.projectionMatrix.clone().invert();
      const camPos = new THREE.Vector3().applyMatrix4(invProj);
      const mouse = new THREE.Vector3(ndc.x, ndc.y, 1).applyMatrix4(invProj);
      const dir = mouse.sub(camPos).normalize();
      return { camPos, dir };
    },

    _raycastAt(point) {
      if (!this.modelLoaded) return [];
      const { camPos, dir } = this._screenToRay(point);
      this.raycaster.set(camPos, dir);
      return this.raycaster.intersectObjects(this.pickables, true)
        .filter(h => this._isVisibleInTree(h.object));
    },

    _isVisibleInTree(obj) {
      let cur = obj;
      while (cur) {
        if (cur.visible === false) return false;
        cur = cur.parent;
      }
      return true;
    },

    // Utilities
    meshCenterLngLat(mesh) {
      if (!mesh?.geometry) return null;
      if (!mesh.geometry.boundingSphere) mesh.geometry.computeBoundingSphere();
      const cLocal = mesh.geometry.boundingSphere.center.clone();
      mesh.updateWorldMatrix(true, false);
      const cWorld = cLocal.applyMatrix4(mesh.matrixWorld);
      const s = transform.scale;
      const x = transform.translateX + cWorld.x * s;
      const y = transform.translateY + cWorld.y * -s;
      const z = transform.translateZ + cWorld.z * s;
      return new mapboxgl.MercatorCoordinate(x, y, z).toLngLat();
    },

    worldToLngLat(worldVec3) {
      const s = transform.scale;
      const x = transform.translateX + worldVec3.x * s;
      const y = transform.translateY + worldVec3.y * -s;
      const z = transform.translateZ + worldVec3.z * s;
      return new mapboxgl.MercatorCoordinate(x, y, z).toLngLat();
    },

    _getWorldBBoxAll() {
      if (!this.modelLoaded || !this.model) return null;
      const box = new THREE.Box3();
      let has = false;
      this.model.traverse((o) => {
        if (!o.isMesh || !o.geometry) return;
        if (!o.geometry.boundingBox) o.geometry.computeBoundingBox();
        o.updateWorldMatrix(true, false);
        const bb = o.geometry.boundingBox.clone().applyMatrix4(o.matrixWorld);
        if (!has) { box.copy(bb); has = true; } else { box.union(bb); }
      });
      return has ? box : null;
    },

    fitShadowToModel(margin = 20) {
      const light = this._light;
      if (!light || !light.shadow || !light.shadow.camera) return false;

      const bb = this._getWorldBBoxAll();
      if (!bb) return false;

      const pts = [
        new THREE.Vector3(bb.min.x, bb.min.y, bb.min.z),
        new THREE.Vector3(bb.min.x, bb.min.y, bb.max.z),
        new THREE.Vector3(bb.min.x, bb.max.y, bb.min.z),
        new THREE.Vector3(bb.min.x, bb.max.y, bb.max.z),
        new THREE.Vector3(bb.max.x, bb.min.y, bb.min.z),
        new THREE.Vector3(bb.max.x, bb.min.y, bb.max.z),
        new THREE.Vector3(bb.max.x, bb.max.y, bb.min.z),
        new THREE.Vector3(bb.max.x, bb.max.y, bb.max.z),
      ];

      light.updateMatrixWorld();
      const cam = light.shadow.camera;
      cam.updateMatrixWorld(true);
      const inv = cam.matrixWorldInverse.clone();

      let min = new THREE.Vector3(+Infinity, +Infinity, +Infinity);
      let max = new THREE.Vector3(-Infinity, -Infinity, -Infinity);

      for (const p of pts) {
        const q = p.clone().applyMatrix4(inv);
        min.min(q); max.max(q);
      }

      min.x -= margin; min.y -= margin; min.z -= margin;
      max.x += margin; max.y += margin; max.z += margin;

      cam.left = min.x;
      cam.right = max.x;
      cam.bottom = min.y;
      cam.top = max.y;
      cam.near = Math.max(0.1, -max.z);
      cam.far = Math.max(cam.near + 1, -min.z);

      cam.updateProjectionMatrix();
      this._shadowCamHelper?.update?.();
      return true;
    },

    setShadowBounds(bounds = {}) {
      const cam = this._light?.shadow?.camera;
      if (!cam) return false;
      Object.assign(cam, bounds);
      cam.updateProjectionMatrix();
      this._shadowCamHelper?.update?.();
      return true;
    },

    setEnabled(v) {
      this._enabled = !!v;
      if (!this._enabled) {
        // clear hover/selection state visually
        if (this._hovered && this._hovered.material) {
          if (hoverStyle === 'opacity') {
            this._hovered.material.opacity = (this._hovered._baseOpacity ?? baseOpacity);
          } else {
            this._hovered.material = this._hovered._origMat || this._hovered.material;
          }
        }
        this._hovered = null;

        for (const o of this._selected) {
          if (hoverStyle === 'opacity') o.material.opacity = (o._baseOpacity ?? baseOpacity);
          else o.material = o._origMat || o.material;
          o.selected = false;
        }
        this._selected.clear();

        // hide debug helpers while disabled
        this._prevDebugVisible = this._lightHelper?.visible;
        if (this._lightHelper) this._lightHelper.visible = false;
        if (this._shadowCamHelper) this._shadowCamHelper.visible = false;
      } else if (this._prevDebugVisible != null) {
        if (this._lightHelper) this._lightHelper.visible = this._prevDebugVisible;
        if (this._shadowCamHelper) this._shadowCamHelper.visible = this._prevDebugVisible;
        this._prevDebugVisible = null;
      }

      this.map?.triggerRepaint?.();
    },

    // expose last progress ratio
    getProgress() {
      return this._lastProgress;
    }
  };

  // keep the opts for later reference from outside
  L.__opts = {
    id,
    modelUrl,
    originLngLat,
    interaction,
    pickFilter,
    meshStyle,
    onHover,
    onClick,
    hoverStyle,
    baseOpacity,
    hoverOpacity,
    selectedOpacity,
    retainHighlightOnSelect,
    shadowPlane,
    bigContextLight,
    lightConfig,
    shadowConfig,
    debugLight,
    onProgress,
    onLoaded
  };

  return L;
}

